/**
 * NextJS-compatible domain detection utilities
 * Works in both server and client environments
 */

import type { NextRequest } from "next/server";
import {
  isLocalhostHost,
  isNetlifyHost as isNetlifyHostName,
  isPlatformHost as isPlatformHostName,
} from "@/lib/domains/constants";

export interface DomainInfo {
  hostname: string;
  port: string | null;
  protocol: string;
  origin: string;
  isLocalhost: boolean;
  isProduction: boolean;
  isDevelopment: boolean;
  isCustomDomain: boolean;
  isPlatformHost: boolean;
  isNetlifyHost: boolean;
  cookieDomain: string;
  viteAppOrigin: string;
  nextjsAppOrigin: string;
}

export interface EnvironmentConfig {
  isProduction: boolean;
  isDevelopment: boolean;
  isServer: boolean;
  isClient: boolean;
  nodeEnv: string;
}

/**
 * Get domain information for the current environment
 */
export function getDomainInfo(request?: Request | NextRequest): DomainInfo {
  const isServer = typeof window === "undefined";

  if (isServer && request) {
    return getServerDomainInfo(request);
  } else if (!isServer) {
    return getClientDomainInfo();
  } else {
    // Fallback for server-side without request
    return getDefaultDomainInfo();
  }
}

/**
 * Get domain information from server-side request
 */
function getServerDomainInfo(request: Request | NextRequest): DomainInfo {
  const url = new URL(request.url);
  const rawOriginalHost = request.headers.get("x-original-host");
  const rawForwardedHost = request.headers.get("x-forwarded-host");
  const rawHost = request.headers.get("host");

  const hostCandidates = [rawOriginalHost, rawForwardedHost, rawHost]
    .filter(Boolean)
    .flatMap((value) => (value ? value.split(",") : []))
    .map((value) => value.trim().split(":")[0].toLowerCase())
    .filter(Boolean);

  const hostname =
    hostCandidates.find(
      (candidate) =>
        !isPlatformHostName(candidate) && !isNetlifyHostName(candidate)
    ) ||
    hostCandidates[0] ||
    url.hostname.toLowerCase();

  // Add console log for debugging
  console.log("[getDomainInfo]", {
    hostname,
    rawOriginalHost,
    rawForwardedHost,
    rawHost,
    url: request.url,
  });

  const port = url.port || null;
  const protocol =
    request.headers.get("x-forwarded-proto") || url.protocol || "https:";
  const origin = `${protocol}//${hostname}`;

  const isLocalhost = isLocalhostHost(hostname);
  const isPlatformHost = isPlatformHostName(hostname);
  const isNetlifyHost = isNetlifyHostName(hostname);
  const isCustomDomain = !isLocalhost && !isPlatformHost && !isNetlifyHost;
  const isDevelopment = isLocalhost || process.env.NODE_ENV === "development";
  const isProduction = isPlatformHost;

  // Add console log to see what's detected
  console.log("[getDomainInfo] Detection:", {
    isLocalhost,
    isPlatformHost,
    isNetlifyHost,
    isCustomDomain,
    isDevelopment,
    isProduction,
  });

  return {
    hostname,
    port,
    protocol,
    origin,
    isLocalhost,
    isProduction,
    isDevelopment,
    isCustomDomain,
    isPlatformHost,
    isNetlifyHost,
    cookieDomain: isPlatformHost ? ".world-class.design" : "",
    viteAppOrigin: isDevelopment
      ? "http://localhost:8080"
      : "https://app.world-class.design",
    nextjsAppOrigin: isDevelopment ? "http://localhost:3000" : origin,
  };
}

/**
 * Get domain information from client-side
 */
function getClientDomainInfo(): DomainInfo {
  const hostname = window.location.hostname;
  const port = window.location.port || null;
  const protocol = window.location.protocol;
  const origin = window.location.origin;

  const isLocalhost = isLocalhostHost(hostname);
  const isPlatformHost = isPlatformHostName(hostname);
  const isNetlifyHost = isNetlifyHostName(hostname);
  const isCustomDomain = !isLocalhost && !isPlatformHost && !isNetlifyHost;
  const isDevelopment = isLocalhost || process.env.NODE_ENV === "development";
  const isProduction = isPlatformHost;

  return {
    hostname,
    port,
    protocol,
    origin,
    isLocalhost,
    isProduction,
    isDevelopment,
    isCustomDomain,
    isPlatformHost,
    isNetlifyHost,
    cookieDomain: isPlatformHost ? ".world-class.design" : "",
    viteAppOrigin: isDevelopment
      ? "http://localhost:8080"
      : "https://app.world-class.design",
    nextjsAppOrigin: isDevelopment ? "http://localhost:3000" : origin,
  };
}

/**
 * Get default domain information for server-side without request
 */
function getDefaultDomainInfo(): DomainInfo {
  const isProduction = process.env.NODE_ENV === "production";
  const isDevelopment = process.env.NODE_ENV === "development";
  const hostname = isProduction ? "world-class.design" : "localhost";
  const isLocalhost = isLocalhostHost(hostname);
  const isPlatformHost = isPlatformHostName(hostname);
  const isNetlifyHost = isNetlifyHostName(hostname);
  const isCustomDomain = !isLocalhost && !isPlatformHost && !isNetlifyHost;

  return {
    hostname,
    port: isProduction ? null : "3000",
    protocol: isProduction ? "https:" : "http:",
    origin: isProduction
      ? "https://world-class.design"
      : "http://localhost:3000",
    isLocalhost: !isProduction,
    isProduction,
    isDevelopment,
    isCustomDomain,
    isPlatformHost,
    isNetlifyHost,
    cookieDomain: isPlatformHost ? ".world-class.design" : "",
    viteAppOrigin: isDevelopment
      ? "http://localhost:8080"
      : "https://app.world-class.design",
    nextjsAppOrigin: isDevelopment
      ? "http://localhost:3000"
      : "https://world-class.design",
  };
}

/**
 * Get environment configuration
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const isServer = typeof window === "undefined";
  const isClient = !isServer;
  const nodeEnv = process.env.NODE_ENV || "development";

  return {
    isProduction: nodeEnv === "production",
    isDevelopment: nodeEnv === "development",
    isServer,
    isClient,
    nodeEnv,
  };
}

/**
 * Check if current domain is production
 */
export function isProductionDomain(): boolean {
  if (typeof window === "undefined") {
    return process.env.NODE_ENV === "production";
  }

  return (
    window.location.hostname.includes("world-class.design") &&
    !window.location.hostname.includes("localhost")
  );
}

/**
 * Check if current domain is development
 */
export function isDevelopmentDomain(): boolean {
  if (typeof window === "undefined") {
    return process.env.NODE_ENV === "development";
  }

  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
}

/**
 * Get cookie domain for cross-subdomain sharing
 */
export function getCookieDomain(): string {
  if (isProductionDomain()) {
    return ".world-class.design";
  }
  return "";
}

/**
 * Get Vite app origin for current environment
 */
export function getViteAppOrigin(): string {
  if (isProductionDomain()) {
    return "https://app.world-class.design";
  }
  return "http://localhost:8080";
}

/**
 * Get NextJS app origin for current environment
 */
export function getNextJSAppOrigin(): string {
  if (isProductionDomain()) {
    return "https://world-class.design";
  }
  return "http://localhost:3000";
}

/**
 * Check if origin is allowed for postMessage communication
 */
export function isAllowedOrigin(origin: string): boolean {
  const allowedOrigins = [
    "http://localhost:3000",
    "http://localhost:8080",
    "https://world-class.design",
    "https://app.world-class.design",
  ];

  return allowedOrigins.includes(origin);
}

/**
 * Get debug information for troubleshooting
 */
export function getDebugInfo(): Record<string, any> {
  const domainInfo = getDomainInfo();
  const envConfig = getEnvironmentConfig();

  return {
    domain: domainInfo,
    environment: envConfig,
    timestamp: new Date().toISOString(),
    userAgent:
      typeof window !== "undefined" ? window.navigator.userAgent : "server",
    cookies: typeof document !== "undefined" ? document.cookie : "server",
    localStorage:
      typeof window !== "undefined"
        ? {
            available: !!window.localStorage,
            keys: window.localStorage ? Object.keys(window.localStorage) : [],
          }
        : "server",
  };
}

/**
 * Validate domain configuration
 */
export function validateDomainConfig(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const domainInfo = getDomainInfo();

  // Check if we're in a valid environment
  if (!domainInfo.isProduction && !domainInfo.isDevelopment) {
    errors.push("Invalid environment: neither production nor development");
  }

  // Check cookie domain configuration
  if (domainInfo.isProduction && !domainInfo.cookieDomain) {
    errors.push("Production environment missing cookie domain configuration");
  }

  // Check origins
  if (!domainInfo.viteAppOrigin || !domainInfo.nextjsAppOrigin) {
    errors.push("Missing app origins configuration");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get storage type based on environment
 */
export function getStorageType(): "cookie" | "localStorage" | "hybrid" {
  if (isProductionDomain()) {
    return "cookie"; // Use cookies for cross-subdomain sharing
  }
  return "hybrid"; // Use both for development
}

/**
 * Check if cross-domain communication is enabled
 */
export function isCrossDomainEnabled(): boolean {
  const domainInfo = getDomainInfo();
  return domainInfo.isProduction || domainInfo.isDevelopment;
}

/**
 * Get postMessage target origins
 */
export function getPostMessageTargets(): string[] {
  const domainInfo = getDomainInfo();

  if (domainInfo.isProduction) {
    return ["https://app.world-class.design", "https://world-class.design"];
  }

  return ["http://localhost:8080", "http://localhost:3000"];
}
